import React, { useState, useEffect } from "react";
// import Logo from "./Logo";
import {
  Box,
  Button,
  Card,
  Typography,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { GetApp, Close, PhoneAndroid } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const PWAInstall: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  useEffect(() => {
    // Check if already installed
    const isInstalled =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;

    if (isInstalled) {
      return;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);

      // Show install prompt after a delay
      setTimeout(() => {
        setShowInstallPrompt(true);
      }, 3000);
    };

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      setShowInstallPrompt(false);
      setSnackbarMessage("Keep Kid Busy has been installed successfully! 🎉");
      setSnackbarSeverity("success");
      setShowSnackbar(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === "accepted") {
        setSnackbarMessage("Installation started! Check your home screen.");
        setSnackbarSeverity("success");
      } else {
        setSnackbarMessage(
          "Installation was dismissed. You can install later from your browser menu."
        );
        setSnackbarSeverity("error");
      }

      setShowSnackbar(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    } catch (error) {
      // Installation failed
      setSnackbarMessage("Installation failed. Please try again.");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    // Don't show again for 24 hours
    localStorage.setItem("pwa-install-dismissed", Date.now().toString());
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  // Check if user dismissed recently
  useEffect(() => {
    const dismissed = localStorage.getItem("pwa-install-dismissed");
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const hoursSinceDismissed =
        (Date.now() - dismissedTime) / (1000 * 60 * 60);

      if (hoursSinceDismissed < 24) {
        setShowInstallPrompt(false);
      } else {
        localStorage.removeItem("pwa-install-dismissed");
      }
    }
  }, []);

  if (!showInstallPrompt) {
    return null;
  }

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          style={{
            position: "fixed",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            maxWidth: "90vw",
            width: "400px",
          }}
        >
          <Card
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #FF6B6B, #FF8E53)",
              color: "white",
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <GetApp sx={{ mr: 1, fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                Install Keep Kid Busy
              </Typography>
              <IconButton
                onClick={handleDismiss}
                sx={{ color: "white", p: 0.5 }}
                size="small"
              >
                <Close />
              </IconButton>
            </Box>

            <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
              Get the full Keep Kid Busy experience with quick access, offline
              support, and app-like performance. Install now for the best
              experience!
            </Typography>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
              <PhoneAndroid sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Works on mobile & desktop
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleInstallClick}
                startIcon={<GetApp />}
                sx={{
                  bgcolor: "white",
                  color: "#FF6B6B",
                  fontWeight: 600,
                  flex: 1,
                  "&:hover": {
                    bgcolor: "#FAFAFA",
                  },
                }}
              >
                Install App
              </Button>
              <Button
                variant="outlined"
                onClick={handleDismiss}
                sx={{
                  borderColor: "rgba(255,255,255,0.5)",
                  color: "white",
                  "&:hover": {
                    borderColor: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Later
              </Button>
            </Box>
          </Card>
        </motion.div>
      </AnimatePresence>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PWAInstall;
